import type { AssignmentCreateInput, AssignmentUpdateInput, AssetsService, AssetCreateInput, AssetUpdateInput, AssetAssignmentCreateInput, AssetAssignmentReturnInput, AssetMaintenanceCreateInput, FarmersService, FarmerRegistrationInput, FarmerUpdateInput, FarmerGroupReference, FarmerGroupCreateInput, AgreementRequirementReference, ConsentTemplateReference, FarmsService, FinanceService, HarvestService, HarvestSeasonCreateInput, HarvestPhysicalDeliveryCreateInput, InputsService, NurseryService, CatalogueItemCreateInput, NurseryVarietyCreateInput, NurserySiteCreateInput, NurseryBedCreateInput, NurserySeedlingLotCreateInput, NurseryLotVarietyDetailInput, NurseryPopulationAssessmentInput, NurseryActivityCreateInput, SeedlingDistributionPlanCreateInput, SeedlingDistributionIssueInput, SeedlingDistributionVarietyDetailInput, InventoryService, ListResult, MediaService, QueryOptions, StaffCreateInput, StaffService, StaffUpdateInput, FarmerAllocation, FarmerAllocationBulkInput, TransactionsService, VisitsService, VisitPlanCreateInput, VisitPlanTargetUpdateInput, VisitStartInput, UnscheduledVisitStartInput, VisitActivityInput, VisitFollowUpInput, VisitCompleteInput, VisitEvidenceUploadInput, LedgerAccountCreateInput, JournalCreateInput, PaymentRequestCreateInput, ProgrammeExpenseCreateInput, FarmerAdvanceCreateInput, FarmerAdvanceDisburseInput, FarmerAdvanceRecoveryCreateInput, CommercialService, AccountsService, BuyingPriceCreateInput, DeliveryCreateInput, CommercialTransactionCreateInput, FarmerPaymentCreateInput, AgreementService, AgreementCreateInput, AmendmentCreateInput, AmendmentExecuteInput, GeographyService, DashboardService, DashboardSummary } from "@/services/contracts";
import type { Asset, AssetAssignment, AssetMaintenance, DocumentRecord, FarmerDetail, FarmerTransaction, FieldVisit, HarvestDelivery, HarvestSeason, InputDistribution, InventoryBalance, InventoryLocation, StockMovement, StaffDetail, FiscalPeriod, LedgerAccount, JournalEntry, PaymentRequest, BuyingPrice, CommercialDelivery, CommercialTransaction, FarmerPayment, FarmerBalance, FarmerAdvance, FarmerAdvanceRecovery, FarmerAgreement, AgreementAmendment, GeographicUnit } from "@/types/domain";
import { mapBuyingPrice, mapDelivery, mapHarvestSeason, mapFarmerHarvestSeasonSummary, mapHarvestRegionSeasonSummary, mapHarvestPlotContext, mapCommercialTxn, mapFarmerPayment, mapFarmerBalance, mapFarmerAdvance, mapFarmerAdvanceRecovery, mapAgreement, mapAmendment, mapGeographicUnit, type ApiBuyingPrice, type ApiDelivery, type ApiHarvestSeason, type ApiFarmerHarvestSeasonSummary, type ApiHarvestRegionSeasonSummary, type ApiHarvestPlotContext, type ApiCommercialTxn, type ApiFarmerPayment, type ApiFarmerBalance, type ApiFarmerAdvance, type ApiFarmerAdvanceRecovery, type ApiAgreement, type ApiAmendment, type ApiGeographicUnit } from "@/services/http/mappers";
import { apiRequest } from "@/services/http/client";
import { ApiAsset, ApiAssetAssignment, ApiAssetMaintenance, ApiConsentTemplate, ApiDistribution, ApiDocument, ApiFarm, ApiFarmer, ApiFarmerGroup, ApiHarvest, ApiInput, ApiNurseryVariety, ApiNurserySite, ApiNurseryBed, ApiNurserySeedlingLot, ApiNurseryPopulationAssessment, ApiNurseryActivityDefinition, ApiNurseryActivity, ApiNurseryStockSummary, ApiSeedlingDistributionPlan, ApiSeedlingDistributionTarget, ApiSeedlingDistributionSource, ApiSeedlingDistributionVariety, ApiInventoryBalance, ApiInventoryLocation, ApiList, ApiPlot, ApiStaff, ApiStockMovement, ApiVisit, ApiVisitPlan, ApiVisitPlanTarget, ApiVisitReason, ApiVisitActivityDefinition, ApiVisitActivity, ApiVisitPhotoEvidence, ApiVisitFollowUp, ApiFiscalPeriod, ApiLedgerAccount, ApiJournalEntry, ApiPaymentRequest, mapAsset, mapAssetAssignment, mapAssetMaintenance, mapDistribution, mapDocument, mapFarm, mapFarmer, mapHarvest, mapInput, mapNurseryVariety, mapNurserySite, mapNurseryBed, mapNurserySeedlingLot, mapNurseryPopulationAssessment, mapNurseryActivityDefinition, mapNurseryActivity, mapNurseryStock, mapSeedlingDistributionPlan, mapSeedlingDistributionTarget, mapSeedlingDistributionSource, mapSeedlingDistributionVariety, mapInventoryBalance, mapInventoryLocation, mapStaff, mapStockMovement, mapVisit, mapVisitPlan, mapVisitPlanTarget, mapVisitReason, mapVisitActivityDefinition, mapVisitActivity, mapVisitEvidence, mapVisitFollowUp, mapFiscalPeriod, mapLedgerAccount, mapJournalEntry, mapPaymentRequest, toApiId } from "@/services/http/mappers";

const query = (options?: QueryOptions) => ({ q: options?.search, record_status: options?.status, sort: options?.sort, order: options?.order, page: options?.page || 1, page_size: options?.pageSize || 25 });
const list = <A, T>(response: ApiList<A>, mapper: (record: A) => T): ListResult<T> => ({ items: response.items.map(mapper), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages });

class HttpFarmersService implements FarmersService {
  private mapGroup(record: ApiFarmerGroup): FarmerGroupReference {
    return {
      id: record.id,
      groupCode: record.group_code,
      groupName: record.group_name,
      geographicUnitId: record.geographic_unit_id,
      chairpersonName: record.chairperson_name,
      contactPhone: record.contact_phone,
      registrationDate: record.registration_date,
      status: record.status,
    };
  }

  async create(input: FarmerRegistrationInput, idempotencyKey = crypto.randomUUID()) {
    const body = {
      client_generated_id: idempotencyKey,
      first_name: input.firstName,
      last_name: input.lastName,
      sex: input.sex,
      nationality: input.nationality,
      geographic_unit_id: input.geographicUnitId,
      geographic_unit_label: input.geographicUnitLabel,
      consent_obtained: input.consentObtained,
      consent_template_id: input.consentTemplateId,
      farmer_group_id: input.farmerGroupId,
      farmer_group_name: input.farmerGroupName,
      birth_year_estimated: input.birthYearEstimated,
      national_id_number: input.nationalIdNumber,
      contact: input.contact ? {
        primary_phone: input.contact.primaryPhone,
        alternative_phone: input.contact.alternativePhone,
        email: input.contact.email,
      } : undefined,
      household: input.household ? {
        declared_household_size: input.household.declaredHouseholdSize,
        declared_dependants: input.household.declaredDependants,
        dependants_in_household: input.household.dependantsInHousehold,
        dependants_elsewhere: input.household.dependantsElsewhere,
        dependants_under_18: input.household.dependantsUnder18,
        notes: input.household.notes,
      } : undefined,
      next_of_kin: {
        first_name: input.nextOfKin.firstName,
        middle_name: input.nextOfKin.middleName,
        last_name: input.nextOfKin.lastName,
        relationship_type: input.nextOfKin.relationshipType,
        phone: input.nextOfKin.phone,
        lives_in_household: input.nextOfKin.livesInHousehold,
        notes: input.nextOfKin.notes,
      },
      initial_farm: input.initialFarm ? {
        client_generated_id: input.initialFarm.clientGeneratedId,
        farm_name: input.initialFarm.farmName,
        geographic_unit_id: input.initialFarm.geographicUnitId,
      } : undefined,
    };
    return mapFarmer(await apiRequest<ApiFarmer>("/farmers", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }

  async uploadEvidence(farmerId: string, evidenceType: "national-id-front" | "national-id-back" | "farmer-photo" | "signed-agreement", file: File) {
    const form = new FormData();
    form.append("file", file);
    const record = await apiRequest<{ id: string; evidence_type: string; file_name: string; mime_type: string; size_bytes: number; checksum_sha256: string; status: string }>(`/farmers/${toApiId(farmerId)}/evidence/${evidenceType}`, { method: "POST", body: form });
    return { id: record.id, evidenceType: record.evidence_type, fileName: record.file_name, mimeType: record.mime_type, sizeBytes: record.size_bytes, checksumSha256: record.checksum_sha256, status: record.status };
  }

  async uploadAgreementRequirement(farmerId: string, requirementId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    const record = await apiRequest<{ id: string; evidence_type: string; file_name: string; mime_type: string; size_bytes: number; checksum_sha256: string; status: string }>(`/farmers/${toApiId(farmerId)}/evidence/signed-agreement`, { method: "POST", query: { requirement_id: toApiId(requirementId) }, body: form });
    return { id: record.id, evidenceType: record.evidence_type, fileName: record.file_name, mimeType: record.mime_type, sizeBytes: record.size_bytes, checksumSha256: record.checksum_sha256, status: record.status };
  }

  async listGroups(includeInactive = false): Promise<FarmerGroupReference[]> {
    const records = await apiRequest<ApiFarmerGroup[]>("/farmer-groups", { query: { include_inactive: includeInactive ? "true" : "false" } });
    return records.map((record) => this.mapGroup(record));
  }

  async createGroup(input: FarmerGroupCreateInput): Promise<FarmerGroupReference> {
    const record = await apiRequest<ApiFarmerGroup>("/farmer-groups", {
      method: "POST",
      body: JSON.stringify({
        group_code: input.groupCode,
        group_name: input.groupName,
        geographic_unit_id: input.geographicUnitId,
        chairperson_name: input.chairpersonName,
        contact_phone: input.contactPhone,
        registration_date: input.registrationDate,
      }),
    });
    return this.mapGroup(record);
  }

  async updateGroup(id: string, input: Partial<FarmerGroupCreateInput> & { status?: "ACTIVE" | "INACTIVE" }): Promise<FarmerGroupReference> {
    const record = await apiRequest<ApiFarmerGroup>(`/farmer-groups/${toApiId(id)}`, {
      method: "PATCH",
      body: JSON.stringify({
        group_name: input.groupName,
        geographic_unit_id: input.geographicUnitId,
        chairperson_name: input.chairpersonName,
        contact_phone: input.contactPhone,
        registration_date: input.registrationDate,
        status: input.status,
      }),
    });
    return this.mapGroup(record);
  }

  async listAgreementRequirements(farmerId: string): Promise<AgreementRequirementReference[]> {
    const rows = await apiRequest<Array<{ id: string; farmer_id: string; plot_id?: string; requirement_type: AgreementRequirementReference["requirementType"]; status: AgreementRequirementReference["status"]; required_at: string; satisfied_at?: string }>>(`/farmers/${toApiId(farmerId)}/agreement-requirements`);
    return rows.map((row) => ({
      id: row.id,
      farmerId: row.farmer_id,
      plotId: row.plot_id,
      requirementType: row.requirement_type,
      status: row.status,
      requiredAt: row.required_at,
      satisfiedAt: row.satisfied_at,
    }));
  }

  async getActiveConsentTemplate(): Promise<ConsentTemplateReference> {
    const record = await apiRequest<ApiConsentTemplate>("/consent-templates/active");
    return { id: record.id, consentCode: record.consent_code, version: record.version, contentSha256: record.content_sha256, effectiveFrom: record.effective_from, exactText: record.exact_text };
  }

  async list(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiFarmer>>("/farmers", { query: query(options) }), mapFarmer); }
  async get(id: string) { return mapFarmer(await apiRequest<ApiFarmer>(`/farmers/${toApiId(id)}`)); }

  async update(id: string, changes: FarmerUpdateInput) {
    const body = {
      first_name: changes.firstName,
      middle_name: changes.middleName,
      last_name: changes.lastName,
      sex: changes.sex,
      birth_year_estimated: changes.birthYearEstimated,
      marital_status: changes.maritalStatus,
      education_level: changes.educationLevel,
      primary_occupation: changes.primaryOccupation,
      geographic_unit_id: changes.geographicUnitId,
      farmer_group_id: changes.farmerGroupId,
      national_id_number: changes.nationalIdNumber,
      contact: changes.contact ? {
        primary_phone: changes.contact.primaryPhone,
        alternative_phone: changes.contact.alternativePhone,
        email: changes.contact.email,
      } : undefined,
      household: changes.household ? {
        declared_household_size: changes.household.declaredHouseholdSize,
        declared_dependants: changes.household.declaredDependants,
        dependants_in_household: changes.household.dependantsInHousehold,
        dependants_elsewhere: changes.household.dependantsElsewhere,
        dependants_under_18: changes.household.dependantsUnder18,
        notes: changes.household.notes,
      } : undefined,
      next_of_kin: changes.nextOfKin ? {
        first_name: changes.nextOfKin.firstName,
        middle_name: changes.nextOfKin.middleName,
        last_name: changes.nextOfKin.lastName,
        relationship_type: changes.nextOfKin.relationshipType,
        phone: changes.nextOfKin.phone,
        lives_in_household: changes.nextOfKin.livesInHousehold,
        notes: changes.nextOfKin.notes,
      } : undefined,
      base_row_version: changes.rowVersion,
    };
    return mapFarmer(await apiRequest<ApiFarmer>(`/farmers/${toApiId(id)}`, { method: "PATCH", body: JSON.stringify(body) }));
  }
  async submit(id: string) { return mapFarmer(await apiRequest<ApiFarmer>(`/farmers/${toApiId(id)}/submit`, { method: "POST" })); }
  async approve(id: string) { return mapFarmer(await apiRequest<ApiFarmer>(`/farmers/${toApiId(id)}/approve`, { method: "POST" })); }
  async archive(id: string, reason: string) { return mapFarmer(await apiRequest<ApiFarmer>(`/farmers/${toApiId(id)}/archive`, { method: "POST", body: JSON.stringify({ reason }) })); }
}
class HttpFarmsService implements FarmsService {
  private async listVisiblePlots(): Promise<ApiPlot[]> {
    const first = await apiRequest<ApiList<ApiPlot>>("/plots", { query: { page: 1, page_size: 100 } });
    if (first.total_pages <= 1) return first.items;
    const remaining = await Promise.all(
      Array.from({ length: first.total_pages - 1 }, (_, index) =>
        apiRequest<ApiList<ApiPlot>>("/plots", { query: { page: index + 2, page_size: 100 } }),
      ),
    );
    return [first, ...remaining].flatMap((page) => page.items);
  }
  async create(input: { farmerId: string; farmName: string; geographicUnitId: string; reportedAreaAcres?: number; ownershipType?: string }, idempotencyKey = crypto.randomUUID()) { const body = { client_generated_id: idempotencyKey, farmer_id: toApiId(input.farmerId), farm_name: input.farmName, geographic_unit_id: input.geographicUnitId, reported_area_acres: input.reportedAreaAcres, ownership_type: input.ownershipType }; return mapFarm(await apiRequest<ApiFarm>("/farms", { method: "POST", body: JSON.stringify(body), idempotencyKey })); }
  async list(options?: QueryOptions) {
    const [response, plots] = await Promise.all([
      apiRequest<ApiList<ApiFarm>>("/farms", { query: query(options) }),
      this.listVisiblePlots(),
    ]);
    if (!response.items.length) return { items: [], total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
    const byFarm = new Map<string, ApiPlot[]>();
    plots.forEach((plot) => {
      const rows = byFarm.get(plot.farm_id) || [];
      rows.push(plot);
      byFarm.set(plot.farm_id, rows);
    });
    return {
      items: response.items.map((farm) => mapFarm(farm, byFarm.get(farm.id) || [])),
      total: response.total,
      page: response.page,
      pageSize: response.page_size,
      totalPages: response.total_pages,
    };
  }
  async listForFarmer(farmerId: string) { const records = await apiRequest<ApiFarm[]>(`/farmers/${toApiId(farmerId)}/farms`); const items = await Promise.all(records.map(async (farm) => mapFarm(farm, (await apiRequest<ApiList<ApiPlot>>("/plots", { query: { farm_id: farm.id } })).items))); return { items, total: items.length, page: 1, pageSize: Math.max(items.length, 1), totalPages: 1 }; }
  async get(id: string) { const record = await apiRequest<ApiFarm>(`/farms/${toApiId(id)}`); const plots = await apiRequest<ApiList<ApiPlot>>("/plots", { query: { farm_id: record.id } }); return mapFarm(record, plots.items); }
  async updateFarm(id: string, changes: Partial<import("@/types/domain").FarmDetail>, baseVersion?: number) { const body = { farm_name: changes.name, base_row_version: baseVersion }; const record = await apiRequest<ApiFarm>(`/farms/${toApiId(id)}`, { method: "PATCH", body: JSON.stringify(body) }); const plots = await apiRequest<ApiList<ApiPlot>>("/plots", { query: { farm_id: record.id } }); return mapFarm(record, plots.items); }
  async captureMapping(id: string, input: import("@/services/contracts").FarmMappingCaptureInput) { const body = { points: input.points.map((p) => ({ latitude: p.latitude, longitude: p.longitude, accuracy_metres: p.accuracyMetres, captured_at: p.capturedAt })), centre_latitude: input.centreLatitude, centre_longitude: input.centreLongitude, centre_accuracy_metres: input.centreAccuracyMetres, device_identifier: input.deviceIdentifier, actual_activity_datetime: input.actualActivityDatetime, local_created_at: input.localCreatedAt, created_offline: input.createdOffline ?? false }; const record = await apiRequest<ApiFarm>(`/farms/${toApiId(id)}/mapping`, { method: "POST", body: JSON.stringify(body) }); const plots = await apiRequest<ApiList<ApiPlot>>("/plots", { query: { farm_id: record.id } }); return mapFarm(record, plots.items); }
  async verifyMapping(id: string, sessionId: string) { const record = await apiRequest<ApiFarm>(`/farms/${toApiId(id)}/mapping/${toApiId(sessionId)}/verify`, { method: "POST" }); const plots = await apiRequest<ApiList<ApiPlot>>("/plots", { query: { farm_id: record.id } }); return mapFarm(record, plots.items); }
  async createPlot(input: { farmId: string; plotName: string; reportedAreaAcres?: number; ownershipType?: string; currentLandUse?: string }, idempotencyKey = crypto.randomUUID()) { const body = { client_generated_id: idempotencyKey, farm_id: toApiId(input.farmId), plot_name: input.plotName, reported_area_acres: input.reportedAreaAcres, ownership_type: input.ownershipType, current_land_use: input.currentLandUse }; const plot = await apiRequest<ApiPlot>("/plots", { method: "POST", body: JSON.stringify(body), idempotencyKey }); return { id: plot.id, code: plot.plot_code, farmId: plot.farm_id, name: plot.plot_name, reportedAreaAcres: plot.reported_area_acres, gpsAreaAcres: plot.gps_area_acres, ownershipType: plot.ownership_type, currentLandUse: plot.current_land_use, localityName: plot.locality_name, ariseContractStartDate: plot.arise_contract_start_date, ariseContractEndDate: plot.arise_contract_end_date, ariseContractStatus: plot.arise_contract_status, centreLatitude: plot.centre_latitude, centreLongitude: plot.centre_longitude, centreAccuracyMetres: plot.centre_accuracy_metres, status: plot.status, recordStatus: plot.record_status, boundaryCaptured: plot.boundary_captured, boundaryVerified: plot.boundary_verified, mappingSessionId: plot.mapping_session_id, mappingStatus: plot.mapping_status, mappingVerifiedAt: plot.mapping_verified_at, rowVersion: plot.row_version }; }
  async updatePlot(id: string, changes: Partial<import("@/types/domain").PlotRecord>, baseVersion?: number) { const body = { plot_name: changes.name, reported_area_acres: changes.reportedAreaAcres, ownership_type: changes.ownershipType, current_land_use: changes.currentLandUse, locality_name: changes.localityName, arise_contract_start_date: changes.ariseContractStartDate, base_row_version: baseVersion }; const plot = await apiRequest<ApiPlot>(`/plots/${toApiId(id)}`, { method: "PATCH", body: JSON.stringify(body) }); return { id: plot.id, code: plot.plot_code, farmId: plot.farm_id, name: plot.plot_name, reportedAreaAcres: plot.reported_area_acres, gpsAreaAcres: plot.gps_area_acres, ownershipType: plot.ownership_type, currentLandUse: plot.current_land_use, localityName: plot.locality_name, ariseContractStartDate: plot.arise_contract_start_date, ariseContractEndDate: plot.arise_contract_end_date, ariseContractStatus: plot.arise_contract_status, centreLatitude: plot.centre_latitude, centreLongitude: plot.centre_longitude, centreAccuracyMetres: plot.centre_accuracy_metres, status: plot.status, recordStatus: plot.record_status, boundaryCaptured: plot.boundary_captured, boundaryVerified: plot.boundary_verified, mappingSessionId: plot.mapping_session_id, mappingStatus: plot.mapping_status, mappingVerifiedAt: plot.mapping_verified_at, rowVersion: plot.row_version }; }
  async capturePlotMapping(id: string, input: import("@/services/contracts").FarmMappingCaptureInput) { const body = { points: input.points.map((p) => ({ latitude: p.latitude, longitude: p.longitude, accuracy_metres: p.accuracyMetres, captured_at: p.capturedAt })), centre_latitude: input.centreLatitude, centre_longitude: input.centreLongitude, centre_accuracy_metres: input.centreAccuracyMetres, device_identifier: input.deviceIdentifier, actual_activity_datetime: input.actualActivityDatetime, local_created_at: input.localCreatedAt, created_offline: input.createdOffline ?? false }; const plot = await apiRequest<ApiPlot>(`/plots/${toApiId(id)}/mapping`, { method: "POST", body: JSON.stringify(body) }); return { id: plot.id, code: plot.plot_code, farmId: plot.farm_id, name: plot.plot_name, reportedAreaAcres: plot.reported_area_acres, gpsAreaAcres: plot.gps_area_acres, ownershipType: plot.ownership_type, currentLandUse: plot.current_land_use, localityName: plot.locality_name, ariseContractStartDate: plot.arise_contract_start_date, ariseContractEndDate: plot.arise_contract_end_date, ariseContractStatus: plot.arise_contract_status, centreLatitude: plot.centre_latitude, centreLongitude: plot.centre_longitude, centreAccuracyMetres: plot.centre_accuracy_metres, status: plot.status, recordStatus: plot.record_status, boundaryCaptured: plot.boundary_captured, boundaryVerified: plot.boundary_verified, mappingSessionId: plot.mapping_session_id, mappingStatus: plot.mapping_status, mappingVerifiedAt: plot.mapping_verified_at, rowVersion: plot.row_version }; }
  async getPlotSeedlingSummary(plotId: string) { const r=await apiRequest<any>(`/plots/${toApiId(plotId)}/seedling-summary`); return { plotId:r.plot_id, issuedSeedlings:Number(r.issued_seedlings || 0), issuedDistributionCount:Number(r.issued_distribution_count || 0), currentEstimatedStand:Number(r.current_estimated_stand || 0), remainingToReconcile:Number(r.remaining_to_reconcile || 0), standExceedsIssued:Boolean(r.stand_exceeds_issued) }; }
  async listCropEstablishments(plotId: string) { const rows=await apiRequest<any[]>(`/plots/${toApiId(plotId)}/crop-establishments`); return rows.map(r=>({ id:r.id, plotId:r.plot_id, crop:r.crop, species:r.species, variety:r.variety, plantingDate:r.planting_date, plantingYear:r.planting_year, plantingMaterialSource:r.planting_material_source, initialPlantPopulation:r.initial_plant_population, currentEstimatedPopulation:r.current_estimated_population, spacingBetweenRows:r.spacing_between_rows, spacingBetweenPlants:r.spacing_between_plants, shadeSystem:r.shade_system, intercrop:r.intercrop, productionStage:r.production_stage, status:r.status })); }
  async createCropEstablishment(plotId: string, input: Omit<import("@/types/domain").CropEstablishment, "id" | "plotId" | "status">) { const body = { crop: input.crop, species: input.species, variety: input.variety, planting_date: input.plantingDate, planting_material_source: input.plantingMaterialSource, initial_plant_population: input.initialPlantPopulation, spacing_between_rows: input.spacingBetweenRows, spacing_between_plants: input.spacingBetweenPlants, shade_system: input.shadeSystem, intercrop: input.intercrop, production_stage: input.productionStage }; const r = await apiRequest<any>(`/plots/${toApiId(plotId)}/crop-establishments`, { method: "POST", body: JSON.stringify(body) }); return { id:r.id, plotId:r.plot_id, crop:r.crop, species:r.species, variety:r.variety, plantingDate:r.planting_date, plantingYear:r.planting_year, plantingMaterialSource:r.planting_material_source, initialPlantPopulation:r.initial_plant_population, currentEstimatedPopulation:r.current_estimated_population, spacingBetweenRows:r.spacing_between_rows, spacingBetweenPlants:r.spacing_between_plants, shadeSystem:r.shade_system, intercrop:r.intercrop, productionStage:r.production_stage, status:r.status }; }
  async listPopulationAssessments(establishmentId: string) { const rows=await apiRequest<any[]>(`/crop-establishments/${toApiId(establishmentId)}/population-assessments`); return rows.map(r=>({ id:r.id,cropEstablishmentId:r.crop_establishment_id,assessmentDate:r.assessment_date,livePlants:r.live_plants,deadPlants:r.dead_plants,missingPlants:r.missing_plants,replacementPlants:r.replacement_plants,survivalPct:r.survival_pct,mortalityPct:r.mortality_pct,assessedBy:r.assessed_by_employee_id,notes:r.notes })); }
  async createPopulationAssessment(establishmentId: string, input: Omit<import("@/types/domain").PlantPopulationAssessment,"id"|"cropEstablishmentId"|"survivalPct"|"mortalityPct">) { const r=await apiRequest<any>(`/crop-establishments/${toApiId(establishmentId)}/population-assessments`,{method:"POST",body:JSON.stringify({assessment_date:input.assessmentDate,live_plants:input.livePlants,dead_plants:input.deadPlants,missing_plants:input.missingPlants,replacement_plants:input.replacementPlants,assessed_by_employee_id:input.assessedBy,notes:input.notes})}); return {id:r.id,cropEstablishmentId:r.crop_establishment_id,assessmentDate:r.assessment_date,livePlants:r.live_plants,deadPlants:r.dead_plants,missingPlants:r.missing_plants,replacementPlants:r.replacement_plants,survivalPct:r.survival_pct,mortalityPct:r.mortality_pct,assessedBy:r.assessed_by_employee_id,notes:r.notes}; }
  async listPlotAnnualYields(plotId: string) { const rows=await apiRequest<any[]>(`/plots/${toApiId(plotId)}/annual-yields`); return rows.map(r=>({id:r.id,plotId:r.plot_id,cropEstablishmentId:r.crop_establishment_id,programmeYear:r.programme_year,calendarYear:r.calendar_year,expectedYieldKg:r.expected_yield_kg,expectedBasis:r.expected_basis,notes:r.notes})); }
  async upsertPlotAnnualYield(plotId: string, input: Omit<import("@/types/domain").PlotAnnualYield,"id"|"plotId">) { const r=await apiRequest<any>(`/plots/${toApiId(plotId)}/annual-yields`,{method:"PUT",body:JSON.stringify({crop_establishment_id:input.cropEstablishmentId,programme_year:input.programmeYear,calendar_year:input.calendarYear,expected_yield_kg:input.expectedYieldKg,expected_basis:input.expectedBasis,notes:input.notes})}); return {id:r.id,plotId:r.plot_id,cropEstablishmentId:r.crop_establishment_id,programmeYear:r.programme_year,calendarYear:r.calendar_year,expectedYieldKg:r.expected_yield_kg,expectedBasis:r.expected_basis,notes:r.notes}; }
  async listFarmAnnualYields(farmId: string) { const rows=await apiRequest<any[]>(`/farms/${toApiId(farmId)}/annual-yields`); return rows.map(r=>({projectId:r.project_id,farmerId:r.farmer_id,farmId:r.farm_id,calendarYear:r.calendar_year,expectedYieldKg:r.expected_yield_kg,actualHarvestKg:r.actual_harvest_kg,varianceKg:r.variance_kg,variancePct:r.variance_pct})); }
  async verifyPlotMapping(id: string, sessionId: string) { const plot = await apiRequest<ApiPlot>(`/plots/${toApiId(id)}/mapping/${toApiId(sessionId)}/verify`, { method: "POST" }); return { id: plot.id, code: plot.plot_code, farmId: plot.farm_id, name: plot.plot_name, reportedAreaAcres: plot.reported_area_acres, gpsAreaAcres: plot.gps_area_acres, ownershipType: plot.ownership_type, currentLandUse: plot.current_land_use, localityName: plot.locality_name, ariseContractStartDate: plot.arise_contract_start_date, ariseContractEndDate: plot.arise_contract_end_date, ariseContractStatus: plot.arise_contract_status, centreLatitude: plot.centre_latitude, centreLongitude: plot.centre_longitude, centreAccuracyMetres: plot.centre_accuracy_metres, status: plot.status, recordStatus: plot.record_status, boundaryCaptured: plot.boundary_captured, boundaryVerified: plot.boundary_verified, mappingSessionId: plot.mapping_session_id, mappingStatus: plot.mapping_status, mappingVerifiedAt: plot.mapping_verified_at, rowVersion: plot.row_version }; }
}
class HttpStaffService implements StaffService {
  async list(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiStaff>>("/staff", { query: query(options) }), mapStaff); }
  async get(id: string) { return mapStaff(await apiRequest<ApiStaff>(`/staff/${toApiId(id)}`)); }
  async create(input: StaffCreateInput) { return mapStaff(await apiRequest<ApiStaff>("/staff", { method: "POST", body: JSON.stringify(input) })); }
  async update(id: string, changes: StaffUpdateInput) { return mapStaff(await apiRequest<ApiStaff>(`/staff/${toApiId(id)}`, { method: "PATCH", body: JSON.stringify(changes) })); }
  async createAssignment(employeeId: string, input: AssignmentCreateInput) { const record = await apiRequest<Record<string, unknown> & { id: string; project_id: string; role_on_project: string; duty_station_geographic_unit_id?: string; supervisor_id?: string; start_date: string; end_date?: string; allocation_percentage?: number; status: "ACTIVE" | "INACTIVE" | "COMPLETED" }>(`/staff/${toApiId(employeeId)}/assignments`, { method: "POST", body: JSON.stringify(input) }); return { id: record.id, projectId: String(record.project_id), roleOnProject: String(record.role_on_project), dutyStationGeographicUnitId: record.duty_station_geographic_unit_id, supervisorId: record.supervisor_id, startDate: record.start_date, endDate: record.end_date, allocationPercentage: record.allocation_percentage, status: record.status }; }
  async updateAssignment(employeeId: string, assignmentId: string, changes: AssignmentUpdateInput) { const record = await apiRequest<Record<string, unknown> & { id: string; project_id: string; role_on_project: string; duty_station_geographic_unit_id?: string; supervisor_id?: string; start_date: string; end_date?: string; allocation_percentage?: number; status: "ACTIVE" | "INACTIVE" | "COMPLETED" } | null>(`/staff/${toApiId(employeeId)}/assignments/${toApiId(assignmentId)}`, { method: "PATCH", body: JSON.stringify(changes) }); if (!record) return undefined; return { id: record.id, projectId: String(record.project_id), roleOnProject: String(record.role_on_project), dutyStationGeographicUnitId: record.duty_station_geographic_unit_id, supervisorId: record.supervisor_id, startDate: record.start_date, endDate: record.end_date, allocationPercentage: record.allocation_percentage, status: record.status }; }
  private mapFarmerAllocation(record: any): FarmerAllocation { return { farmerId: record.farmer_id, farmerCode: record.farmer_code, farmerName: record.farmer_name, geographicUnitLabel: record.geographic_unit_label, recordStatus: record.record_status, assignedFieldOfficerId: record.assigned_field_officer_id, assignedFieldOfficerName: record.assigned_field_officer_name, assignedAgronomistId: record.assigned_agronomist_id, assignedAgronomistName: record.assigned_agronomist_name }; }
  async listFarmerAllocations(): Promise<FarmerAllocation[]> { const rows = await apiRequest<any[]>("/staff/farmer-allocations"); return rows.map((row) => this.mapFarmerAllocation(row)); }
  async bulkUpdateFarmerAllocations(input: FarmerAllocationBulkInput): Promise<FarmerAllocation[]> { const rows = await apiRequest<any[]>("/staff/farmer-allocations/bulk", { method: "POST", body: JSON.stringify({ assignment_type: input.assignmentType, employee_id: input.employeeId, farmer_ids: input.farmerIds }) }); return rows.map((row) => this.mapFarmerAllocation(row)); }
}
class HttpVisitsService implements VisitsService {
  async list(options?: QueryOptions) {
    return list(await apiRequest<ApiList<ApiVisit>>("/visits", { query: query(options) }), mapVisit);
  }

  async get(id: string) {
    return mapVisit(await apiRequest<ApiVisit>(`/visits/${toApiId(id)}`));
  }

  async create(input: Omit<FieldVisit, "id" | "reference">, idempotencyKey = crypto.randomUUID()) {
    const body = {
      client_generated_id: idempotencyKey,
      visit_date: input.visitDate,
      farmer_id: toApiId(input.farmerId),
      farm_id: toApiId(input.farmId),
      plot_id: input.plotId ? toApiId(input.plotId) : undefined,
      plot_ids: (input.plotIds || []).map(toApiId),
      assigned_staff_id: toApiId(input.assignedStaffId),
      observations: input.observations,
      recommendations: input.recommendations,
      pest_disease_observation: input.pestDiseaseObservation,
      follow_up_date: input.followUpDate,
      follow_up_action: input.followUpAction,
    };
    return mapVisit(await apiRequest<ApiVisit>("/visits", {
      method: "POST", body: JSON.stringify(body), idempotencyKey,
    }));
  }

  async listPlans() {
    return (await apiRequest<ApiVisitPlan[]>("/visit-plans")).map(mapVisitPlan);
  }

  async getPlan(id: string) {
    return mapVisitPlan(await apiRequest<ApiVisitPlan>(`/visit-plans/${toApiId(id)}`));
  }

  async createPlan(input: VisitPlanCreateInput) {
    const body = {
      title: input.title,
      assigned_staff_id: toApiId(input.assignedStaffId),
      planned_start_date: input.plannedStartDate,
      planned_end_date: input.plannedEndDate,
      notes: input.notes,
      targets: input.targets.map((target) => ({
        farmer_id: toApiId(target.farmerId),
        farm_id: target.farmId ? toApiId(target.farmId) : undefined,
        planned_date: target.plannedDate,
        sequence_no: target.sequenceNo,
        notes: target.notes,
      })),
    };
    return mapVisitPlan(await apiRequest<ApiVisitPlan>("/visit-plans", {
      method: "POST", body: JSON.stringify(body),
    }));
  }

  async sanctionPlan(id: string, reason?: string, idempotencyKey = crypto.randomUUID()) {
    return mapVisitPlan(await apiRequest<ApiVisitPlan>(`/visit-plans/${toApiId(id)}/sanction`, {
      method: "POST",
      body: JSON.stringify({ reason }),
      idempotencyKey,
    }));
  }

  async acknowledgePlan(id: string, reason?: string, idempotencyKey = crypto.randomUUID()) {
    return mapVisitPlan(await apiRequest<ApiVisitPlan>(`/visit-plans/${toApiId(id)}/acknowledge`, {
      method: "POST",
      body: JSON.stringify({ reason }),
      idempotencyKey,
    }));
  }

  async updatePlanTarget(planId: string, targetId: string, input: VisitPlanTargetUpdateInput) {
    return mapVisitPlanTarget(await apiRequest<ApiVisitPlanTarget>(
      `/visit-plans/${toApiId(planId)}/targets/${toApiId(targetId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          status: input.status,
          rescheduled_to_date: input.rescheduledToDate,
          missed_reason: input.missedReason,
          notes: input.notes,
        }),
      },
    ));
  }

  async listReasons() {
    return (await apiRequest<ApiVisitReason[]>("/visits/reasons")).map(mapVisitReason);
  }

  async listActivityCatalogue() {
    return (await apiRequest<ApiVisitActivityDefinition[]>("/visits/activities/catalogue"))
      .map(mapVisitActivityDefinition);
  }

  async startPlanned(planId: string, targetId: string, input: VisitStartInput, idempotencyKey = crypto.randomUUID()) {
    const body = {
      client_generated_id: input.clientGeneratedId,
      plot_ids: input.plotIds.map(toApiId),
      reason_ids: input.reasonIds.map(toApiId),
      started_at: input.startedAt,
      latitude: input.latitude,
      longitude: input.longitude,
      gps_accuracy_metres: input.gpsAccuracyMetres,
      device_identifier: input.deviceIdentifier,
      created_offline: input.createdOffline || false,
      local_created_at: input.localCreatedAt,
      observations: input.observations,
    };
    return mapVisit(await apiRequest<ApiVisit>(
      `/visit-plans/${toApiId(planId)}/targets/${toApiId(targetId)}/start`,
      { method: "POST", body: JSON.stringify(body), idempotencyKey },
    ));
  }

  async startUnscheduled(input: UnscheduledVisitStartInput, idempotencyKey = crypto.randomUUID()) {
    const body = {
      client_generated_id: input.clientGeneratedId,
      farmer_id: toApiId(input.farmerId),
      farm_id: toApiId(input.farmId),
      plot_ids: input.plotIds.map(toApiId),
      reason_ids: input.reasonIds.map(toApiId),
      unscheduled_reason: input.unscheduledReason,
      started_at: input.startedAt,
      latitude: input.latitude,
      longitude: input.longitude,
      gps_accuracy_metres: input.gpsAccuracyMetres,
      device_identifier: input.deviceIdentifier,
      created_offline: input.createdOffline || false,
      local_created_at: input.localCreatedAt,
      observations: input.observations,
    };
    return mapVisit(await apiRequest<ApiVisit>("/visits/unscheduled/start", {
      method: "POST", body: JSON.stringify(body), idempotencyKey,
    }));
  }

  async listActivities(visitId: string) {
    return (await apiRequest<ApiVisitActivity[]>(`/visits/${toApiId(visitId)}/activities`))
      .map(mapVisitActivity);
  }

  async listEvidence(visitId: string) {
    return (await apiRequest<ApiVisitPhotoEvidence[]>(`/visits/${toApiId(visitId)}/evidence`))
      .map(mapVisitEvidence);
  }

  async listFollowUps(visitId: string) {
    return (await apiRequest<ApiVisitFollowUp[]>(`/visits/${toApiId(visitId)}/follow-ups`))
      .map(mapVisitFollowUp);
  }

  async addActivity(visitId: string, input: VisitActivityInput) {
    const body = {
      activity_id: toApiId(input.activityId),
      plot_id: input.plotId ? toApiId(input.plotId) : undefined,
      distribution_id: input.distributionId ? toApiId(input.distributionId) : undefined,
      asset_id: input.assetId ? toApiId(input.assetId) : undefined,
      performed_at: input.performedAt,
      notes: input.notes,
      quantity: input.quantity,
      unit: input.unit,
      client_generated_id: input.clientGeneratedId,
      device_identifier: input.deviceIdentifier,
      created_offline: input.createdOffline || false,
    };
    return mapVisitActivity(await apiRequest<ApiVisitActivity>(`/visits/${toApiId(visitId)}/activities`, {
      method: "POST", body: JSON.stringify(body),
    }));
  }

  async uploadEvidence(visitId: string, input: VisitEvidenceUploadInput) {
    const form = new FormData();
    form.append("file", input.file);
    const record = await apiRequest<ApiVisitPhotoEvidence>(
      `/visits/${toApiId(visitId)}/evidence/${input.evidenceType}`,
      {
        method: "POST",
        query: {
          captured_at: input.capturedAt,
          latitude: input.latitude,
          longitude: input.longitude,
          gps_accuracy_metres: input.gpsAccuracyMetres,
          plot_id: input.plotId ? toApiId(input.plotId) : undefined,
          device_identifier: input.deviceIdentifier,
          created_offline: input.createdOffline ? "true" : "false",
          local_created_at: input.localCreatedAt,
          client_generated_id: input.clientGeneratedId,
        },
        body: form,
      },
    );
    return mapVisitEvidence(record);
  }

  async addFollowUp(visitId: string, input: VisitFollowUpInput) {
    const body = {
      action: input.action,
      due_date: input.dueDate,
      assigned_staff_id: input.assignedStaffId ? toApiId(input.assignedStaffId) : undefined,
      recommendation_id: input.recommendationId ? toApiId(input.recommendationId) : undefined,
      client_generated_id: input.clientGeneratedId,
      device_identifier: input.deviceIdentifier,
      created_offline: input.createdOffline || false,
    };
    return mapVisitFollowUp(await apiRequest<ApiVisitFollowUp>(`/visits/${toApiId(visitId)}/follow-ups`, {
      method: "POST", body: JSON.stringify(body),
    }));
  }

  async complete(visitId: string, input: VisitCompleteInput) {
    const body = {
      completed_at: input.completedAt,
      latitude: input.latitude,
      longitude: input.longitude,
      gps_accuracy_metres: input.gpsAccuracyMetres,
      inspected_plot_ids: input.inspectedPlotIds.map(toApiId),
      observations: input.observations,
      recommendations: input.recommendations,
    };
    return mapVisit(await apiRequest<ApiVisit>(`/visits/${toApiId(visitId)}/complete`, {
      method: "POST", body: JSON.stringify(body),
    }));
  }
}
class HttpInputsService implements InputsService {
  async listCatalogue(options?: QueryOptions) {
    return list(await apiRequest<ApiList<ApiInput>>("/inputs", { query: query(options) }), mapInput);
  }

  async createCatalogueItem(input: CatalogueItemCreateInput) {
    return mapInput(await apiRequest<ApiInput>("/inputs", {
      method: "POST",
      body: JSON.stringify({
        item_code: input.itemCode,
        item_name: input.itemName,
        category: input.category,
        default_unit: input.defaultUnit,
        description: input.description,
        stock_tracking_mode: input.stockTrackingMode,
        active: input.active ?? true,
      }),
    }));
  }

  async listDistributions(options?: QueryOptions) {
    return list(await apiRequest<ApiList<ApiDistribution>>("/distributions", { query: query(options) }), mapDistribution);
  }

  async createDistribution(
    input: Omit<InputDistribution, "id" | "reference" | "itemName" | "farmerName" | "responsibleStaff">,
    idempotencyKey = crypto.randomUUID(),
  ) {
    const body = {
      client_generated_id: idempotencyKey,
      farmer_id: toApiId(input.farmerId),
      farm_id: input.farmId ? toApiId(input.farmId) : undefined,
      plot_id: input.plotId ? toApiId(input.plotId) : undefined,
      item_id: toApiId(input.itemId),
      quantity: input.quantity,
      unit: input.unit,
      distribution_date: input.distributionDate,
      responsible_staff_id: toApiId(input.responsibleStaffId),
    };
    return mapDistribution(await apiRequest<ApiDistribution>("/distributions", {
      method: "POST", body: JSON.stringify(body), idempotencyKey,
    }));
  }
}

class HttpNurseryService implements NurseryService {
  async listVarieties() {
    return (await apiRequest<ApiNurseryVariety[]>("/nursery/varieties")).map(mapNurseryVariety);
  }

  async createVariety(input: NurseryVarietyCreateInput) {
    return mapNurseryVariety(await apiRequest<ApiNurseryVariety>("/nursery/varieties", {
      method: "POST",
      body: JSON.stringify({
        variety_code: input.varietyCode,
        variety_name: input.varietyName,
        coffee_type: input.coffeeType || "ROBUSTA",
        source_name: input.sourceName,
        source_region: input.sourceRegion,
        description: input.description,
        active: input.active ?? true,
      }),
    }));
  }

  async listSites() {
    return (await apiRequest<ApiNurserySite[]>("/nursery/sites")).map(mapNurserySite);
  }

  async createSite(input: NurserySiteCreateInput) {
    return mapNurserySite(await apiRequest<ApiNurserySite>("/nursery/sites", {
      method: "POST",
      body: JSON.stringify({
        nursery_code: input.nurseryCode,
        nursery_name: input.nurseryName,
        geographic_unit_id: input.geographicUnitId ? toApiId(input.geographicUnitId) : undefined,
        responsible_employee_id: input.responsibleEmployeeId ? toApiId(input.responsibleEmployeeId) : undefined,
        description: input.description,
      }),
    }));
  }

  async listBeds(nurseryId?: string) {
    return (await apiRequest<ApiNurseryBed[]>("/nursery/beds", {
      query: { nursery_id: nurseryId ? toApiId(nurseryId) : undefined },
    })).map(mapNurseryBed);
  }

  async createBed(input: NurseryBedCreateInput) {
    return mapNurseryBed(await apiRequest<ApiNurseryBed>("/nursery/beds", {
      method: "POST",
      body: JSON.stringify({
        nursery_id: toApiId(input.nurseryId),
        bed_code: input.bedCode,
        bed_name: input.bedName,
        estimated_capacity: input.estimatedCapacity,
        description: input.description,
      }),
    }));
  }

  async listLots() {
    return (await apiRequest<ApiNurserySeedlingLot[]>("/nursery/lots")).map(mapNurserySeedlingLot);
  }

  async getLot(id: string) {
    return mapNurserySeedlingLot(await apiRequest<ApiNurserySeedlingLot>(`/nursery/lots/${toApiId(id)}`));
  }

  async createLot(input: NurserySeedlingLotCreateInput) {
    return mapNurserySeedlingLot(await apiRequest<ApiNurserySeedlingLot>("/nursery/lots", {
      method: "POST",
      body: JSON.stringify({
        nursery_id: toApiId(input.nurseryId),
        bed_id: toApiId(input.bedId),
        seedling_item_id: toApiId(input.seedlingItemId),
        source_name: input.sourceName,
        source_location: input.sourceLocation,
        propagation_started_date: input.propagationStartedDate,
        established_date: input.establishedDate,
        initial_quantity: input.initialQuantity,
        notes: input.notes,
        varieties: (input.varieties || []).map((item) => ({
          variety_id: toApiId(item.varietyId),
          estimated_quantity: item.estimatedQuantity,
          notes: item.notes,
        })),
      }),
    }));
  }

  async updateLotVarieties(id: string, input: NurseryLotVarietyDetailInput) {
    return mapNurserySeedlingLot(await apiRequest<ApiNurserySeedlingLot>(`/nursery/lots/${toApiId(id)}/varieties`, {
      method: "PUT",
      body: JSON.stringify({
        variety_detail_status: input.varietyDetailStatus,
        varieties: input.varieties.map((item) => ({
          variety_id: toApiId(item.varietyId),
          estimated_quantity: item.estimatedQuantity,
          notes: item.notes,
        })),
      }),
    }));
  }

  async listPopulationAssessments(lotId: string) {
    return (await apiRequest<ApiNurseryPopulationAssessment[]>(`/nursery/lots/${toApiId(lotId)}/population-assessments`))
      .map(mapNurseryPopulationAssessment);
  }

  async addPopulationAssessment(lotId: string, input: NurseryPopulationAssessmentInput) {
    return mapNurseryPopulationAssessment(await apiRequest<ApiNurseryPopulationAssessment>(
      `/nursery/lots/${toApiId(lotId)}/population-assessments`,
      {
        method: "POST",
        body: JSON.stringify({
          assessed_at: input.assessedAt,
          current_viable_quantity: input.currentViableQuantity,
          ready_quantity: input.readyQuantity,
          mortality_since_last: input.mortalitySinceLast,
          culled_since_last: input.culledSinceLast,
          variety_detail_status: input.varietyDetailStatus,
          notes: input.notes,
          assessed_by_employee_id: input.assessedByEmployeeId ? toApiId(input.assessedByEmployeeId) : undefined,
          varieties: (input.varieties || []).map((item) => ({
            variety_id: toApiId(item.varietyId),
            quantity: item.quantity,
            notes: item.notes,
          })),
        }),
      },
    ));
  }

  async listActivityCatalogue() {
    return (await apiRequest<ApiNurseryActivityDefinition[]>("/nursery/activities/catalogue"))
      .map(mapNurseryActivityDefinition);
  }

  async listActivities(nurseryId?: string, lotId?: string) {
    return (await apiRequest<ApiNurseryActivity[]>("/nursery/activities", {
      query: {
        nursery_id: nurseryId ? toApiId(nurseryId) : undefined,
        lot_id: lotId ? toApiId(lotId) : undefined,
      },
    })).map(mapNurseryActivity);
  }

  async addActivity(input: NurseryActivityCreateInput) {
    return mapNurseryActivity(await apiRequest<ApiNurseryActivity>("/nursery/activities", {
      method: "POST",
      body: JSON.stringify({
        client_generated_id: input.clientGeneratedId,
        nursery_id: toApiId(input.nurseryId),
        bed_id: input.bedId ? toApiId(input.bedId) : undefined,
        lot_id: input.lotId ? toApiId(input.lotId) : undefined,
        activity_id: toApiId(input.activityId),
        performed_at: input.performedAt,
        notes: input.notes,
        responsible_employee_id: input.responsibleEmployeeId ? toApiId(input.responsibleEmployeeId) : undefined,
        device_identifier: input.deviceIdentifier,
        created_offline: input.createdOffline || false,
        local_created_at: input.localCreatedAt,
        inputs: (input.inputs || []).map((usage) => ({
          input_item_id: toApiId(usage.inputItemId),
          inventory_location_id: toApiId(usage.inventoryLocationId),
          quantity: usage.quantity,
          unit: usage.unit,
          notes: usage.notes,
        })),
      }),
    }));
  }

  async listStock() {
    return (await apiRequest<ApiNurseryStockSummary[]>("/nursery/stock")).map(mapNurseryStock);
  }

  async listDistributionPlans() {
    return (await apiRequest<ApiSeedlingDistributionPlan[]>("/seedling-distribution-plans"))
      .map(mapSeedlingDistributionPlan);
  }

  async getDistributionPlan(id: string) {
    return mapSeedlingDistributionPlan(
      await apiRequest<ApiSeedlingDistributionPlan>(`/seedling-distribution-plans/${toApiId(id)}`),
    );
  }

  async createDistributionPlan(input: SeedlingDistributionPlanCreateInput) {
    return mapSeedlingDistributionPlan(await apiRequest<ApiSeedlingDistributionPlan>("/seedling-distribution-plans", {
      method: "POST",
      body: JSON.stringify({
        title: input.title,
        planned_start_date: input.plannedStartDate,
        planned_end_date: input.plannedEndDate,
        responsible_employee_id: toApiId(input.responsibleEmployeeId),
        notes: input.notes,
        targets: input.targets.map((target) => ({
          farmer_id: toApiId(target.farmerId),
          farm_id: target.farmId ? toApiId(target.farmId) : undefined,
          plot_id: toApiId(target.plotId),
          seedling_item_id: toApiId(target.seedlingItemId),
          planned_quantity: target.plannedQuantity,
          notes: target.notes,
        })),
      }),
    }));
  }

  async sanctionDistributionPlan(id: string, reason?: string) {
    return mapSeedlingDistributionPlan(await apiRequest<ApiSeedlingDistributionPlan>(
      `/seedling-distribution-plans/${toApiId(id)}/sanction`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ));
  }

  async updateDistributionTarget(
    planId: string,
    targetId: string,
    status: "PLANNED" | "READY" | "MISSED" | "CANCELLED",
    notes?: string,
  ) {
    return mapSeedlingDistributionTarget(await apiRequest<ApiSeedlingDistributionTarget>(
      `/seedling-distribution-plans/${toApiId(planId)}/targets/${toApiId(targetId)}`,
      { method: "PATCH", body: JSON.stringify({ status, notes }) },
    ));
  }

  async issueDistributionTarget(planId: string, targetId: string, input: SeedlingDistributionIssueInput) {
    return mapSeedlingDistributionTarget(await apiRequest<ApiSeedlingDistributionTarget>(
      `/seedling-distribution-plans/${toApiId(planId)}/targets/${toApiId(targetId)}/issue`,
      {
        method: "POST",
        body: JSON.stringify({
          actual_received_quantity: input.actualReceivedQuantity,
          distribution_date: input.distributionDate,
          sources: input.sources.map((source) => ({
            nursery_lot_id: toApiId(source.nurseryLotId),
            quantity: source.quantity,
          })),
          variety_detail_status: input.varietyDetailStatus,
          varieties: (input.varieties || []).map((item) => ({
            variety_id: toApiId(item.varietyId),
            quantity: item.quantity,
            notes: item.notes,
          })),
          notes: input.notes,
        }),
      },
    ));
  }

  async listDistributionSources(targetId: string) {
    return (await apiRequest<ApiSeedlingDistributionSource[]>(
      `/seedling-distribution-targets/${toApiId(targetId)}/sources`,
    )).map(mapSeedlingDistributionSource);
  }

  async listDistributionVarieties(targetId: string) {
    return (await apiRequest<ApiSeedlingDistributionVariety[]>(
      `/seedling-distribution-targets/${toApiId(targetId)}/varieties`,
    )).map(mapSeedlingDistributionVariety);
  }

  async updateDistributionVarieties(targetId: string, input: SeedlingDistributionVarietyDetailInput) {
    return mapSeedlingDistributionTarget(await apiRequest<ApiSeedlingDistributionTarget>(
      `/seedling-distribution-targets/${toApiId(targetId)}/varieties`,
      {
        method: "PUT",
        body: JSON.stringify({
          variety_detail_status: input.varietyDetailStatus,
          varieties: input.varieties.map((item) => ({
            variety_id: toApiId(item.varietyId),
            quantity: item.quantity,
            notes: item.notes,
          })),
        }),
      },
    ));
  }
}

class HttpHarvestService implements HarvestService {
  async list(options?: QueryOptions) {
    return list(await apiRequest<ApiList<ApiHarvest>>("/harvest", { query: query(options) }), mapHarvest);
  }
  async get(id: string) {
    const all = await this.list({ search: id });
    return all.items.find((item) => item.id === id);
  }
  async create(input: Omit<HarvestDelivery, "id" | "reference" | "farmerName" | "farmName" | "status" | "paymentStatus">, idempotencyKey = crypto.randomUUID()) {
    const body = { client_generated_id: idempotencyKey, farmer_id: toApiId(input.farmerId), farm_id: toApiId(input.farmId), delivery_date: input.deliveryDate, quantity_kg: input.quantityKg, quality_grade: input.qualityGrade };
    return mapHarvest(await apiRequest<ApiHarvest>("/harvest", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async listSeasons() {
    return (await apiRequest<ApiHarvestSeason[]>("/harvest/seasons")).map(mapHarvestSeason);
  }
  async createSeason(input: HarvestSeasonCreateInput) {
    return mapHarvestSeason(await apiRequest<ApiHarvestSeason>("/harvest/seasons", { method: "POST", body: JSON.stringify(input) }));
  }
  async updateSeasonStatus(id: string, status: HarvestSeason["status"]) {
    return mapHarvestSeason(await apiRequest<ApiHarvestSeason>(`/harvest/seasons/${toApiId(id)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }));
  }
  async listPhysicalDeliveries(options?: QueryOptions) {
    const response = await apiRequest<ApiList<ApiDelivery>>("/harvest/deliveries", { query: query(options) });
    return { items: response.items.map(mapDelivery), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createPhysicalDelivery(input: HarvestPhysicalDeliveryCreateInput, idempotencyKey = crypto.randomUUID()) {
    const body = {
      delivery_reference: input.delivery_reference,
      farmer_id: toApiId(input.farmer_id),
      farm_id: toApiId(input.farm_id),
      harvest_season_id: toApiId(input.harvest_season_id),
      delivered_at: input.delivered_at,
      gross_quantity: input.gross_quantity,
      accepted_quantity: input.accepted_quantity,
      rejected_quantity: input.rejected_quantity,
      unit: input.unit || "kg",
      quality_grade: input.quality_grade,
      moisture_pct: input.moisture_pct,
      product_form: input.product_form || "DRY_CHERRY",
    };
    return mapDelivery(await apiRequest<ApiDelivery>("/harvest/deliveries", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async verifyPhysicalDelivery(id: string) {
    return mapDelivery(await apiRequest<ApiDelivery>(`/harvest/deliveries/${toApiId(id)}/verify`, { method: "POST" }));
  }
  async listSeasonPositions(options?: QueryOptions) {
    const response = await apiRequest<ApiList<ApiFarmerHarvestSeasonSummary>>("/harvest/season-positions", { query: query(options) });
    return { items: response.items.map(mapFarmerHarvestSeasonSummary), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async listRegionSummary() {
    return (await apiRequest<ApiHarvestRegionSeasonSummary[]>("/harvest/region-summary")).map(mapHarvestRegionSeasonSummary);
  }
  async listPlotContext() {
    return (await apiRequest<ApiHarvestPlotContext[]>("/harvest/plot-context")).map(mapHarvestPlotContext);
  }
}
class HttpMediaService implements MediaService { async list(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiDocument>>("/documents", { query: query(options) }), mapDocument); } async get(id: string) { const result = await this.list({ search: id }); return result.items.find((item) => item.id === id); } async createMock(input: Omit<DocumentRecord, "id" | "uploadedAt">, idempotencyKey = crypto.randomUUID()) { const body = { client_generated_id: idempotencyKey, entity_type: input.entityType, entity_id: toApiId(input.entityId), document_type: input.documentType, bucket_id: input.bucketId, file_name: input.fileName, mime_type: input.mimeType, size_bytes: input.sizeBytes, confidential: input.confidential }; return mapDocument(await apiRequest<ApiDocument>("/documents", { method: "POST", body: JSON.stringify(body), idempotencyKey })); } }
class HttpInventoryService implements InventoryService { async listLocations(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiInventoryLocation>>("/inventory/locations", { query: query(options) }), mapInventoryLocation); } async listBalances(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiInventoryBalance>>("/inventory/balances", { query: query(options) }), mapInventoryBalance); } async listMovements(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiStockMovement>>("/inventory/movements", { query: query(options) }), mapStockMovement); } async createReceipt(input: { itemId: string; inventoryLocationId: string; lotNumber?: string; quantity: number; unit: string; supplierName?: string; notes?: string }, idempotencyKey = crypto.randomUUID()) { const body = { item_id: toApiId(input.itemId), inventory_location_id: toApiId(input.inventoryLocationId), lot_number: input.lotNumber, quantity: input.quantity, unit: input.unit, supplier_name: input.supplierName, notes: input.notes }; return mapStockMovement(await apiRequest<ApiStockMovement>("/inventory/receipts", { method: "POST", body: JSON.stringify(body), idempotencyKey })); } async createIssue(input: { itemId: string; inventoryLocationId: string; quantity: number; unit: string; notes?: string }, idempotencyKey = crypto.randomUUID()) { const body = { item_id: toApiId(input.itemId), inventory_location_id: toApiId(input.inventoryLocationId), quantity: input.quantity, unit: input.unit, notes: input.notes }; return mapStockMovement(await apiRequest<ApiStockMovement>("/inventory/issues", { method: "POST", body: JSON.stringify(body), idempotencyKey })); } async createTransfer(input: { itemId: string; fromLocationId: string; toLocationId: string; quantity: number; unit: string; notes?: string }, idempotencyKey = crypto.randomUUID()) { const body = { item_id: toApiId(input.itemId), from_location_id: toApiId(input.fromLocationId), to_location_id: toApiId(input.toLocationId), quantity: input.quantity, unit: input.unit, notes: input.notes }; const records = await apiRequest<ApiStockMovement[]>("/inventory/transfers", { method: "POST", body: JSON.stringify(body), idempotencyKey }); return records.map(mapStockMovement); } async issueDistribution(distributionId: string) { return mapDistribution(await apiRequest<ApiDistribution>(`/distributions/${toApiId(distributionId)}/issue`, { method: "POST" })); } }

class HttpTransactionsService implements TransactionsService {
  async list(options?: QueryOptions): Promise<ListResult<FarmerTransaction>> {
    const response = await apiRequest<ApiList<ApiDistribution | ApiHarvest>>("/transactions", { query: query(options) });
    const items = response.items.map((record): FarmerTransaction => {
      if ("distribution_date" in record) {
        return { id: record.id, reference: record.reference, farmerId: record.farmer_id, farmerName: record.farmer_name ?? "Unknown farmer", date: record.distribution_date, type: "INPUT_DISTRIBUTION", description: record.item_name ?? "Input distribution", quantity: `${record.quantity} ${record.unit}`, status: record.status };
      }
      return { id: record.id, reference: record.reference, farmerId: record.farmer_id, farmerName: record.farmer_name ?? "Unknown farmer", date: record.delivery_date, type: "HARVEST_DELIVERY", description: `Delivery · Grade ${record.quality_grade}`, quantity: `${record.quantity_kg} kg`, status: record.payment_status };
    });
    return { items, total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
}

class HttpAssetsService implements AssetsService {
  async list(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiAsset>>("/assets", { query: query(options) }), mapAsset); }
  async get(id: string) { return mapAsset(await apiRequest<ApiAsset>(`/assets/${toApiId(id)}`)); }
  async create(input: AssetCreateInput) { return mapAsset(await apiRequest<ApiAsset>("/assets", { method: "POST", body: JSON.stringify(input) })); }
  async update(id: string, input: AssetUpdateInput) { return mapAsset(await apiRequest<ApiAsset>(`/assets/${toApiId(id)}`, { method: "PATCH", body: JSON.stringify(input) })); }
  async listAssignments(assetId: string) { return list(await apiRequest<ApiList<ApiAssetAssignment>>(`/assets/${toApiId(assetId)}/assignments`, {}), mapAssetAssignment); }
  async createAssignment(assetId: string, input: AssetAssignmentCreateInput) { return mapAssetAssignment(await apiRequest<ApiAssetAssignment>(`/assets/${toApiId(assetId)}/assignments`, { method: "POST", body: JSON.stringify(input) })); }
  async returnAssignment(assetId: string, assignmentId: string, input: AssetAssignmentReturnInput) { return mapAssetAssignment(await apiRequest<ApiAssetAssignment>(`/assets/${toApiId(assetId)}/assignments/${toApiId(assignmentId)}/return`, { method: "POST", body: JSON.stringify(input) })); }
  async listMaintenance(assetId: string) { return list(await apiRequest<ApiList<ApiAssetMaintenance>>(`/assets/${toApiId(assetId)}/maintenance`, {}), mapAssetMaintenance); }
  async createMaintenance(assetId: string, input: AssetMaintenanceCreateInput) { return mapAssetMaintenance(await apiRequest<ApiAssetMaintenance>(`/assets/${toApiId(assetId)}/maintenance`, { method: "POST", body: JSON.stringify(input) })); }
}

class HttpFinanceService implements FinanceService {
  async listFiscalPeriods() { return list(await apiRequest<ApiList<ApiFiscalPeriod>>("/finance/fiscal-periods", {}), mapFiscalPeriod); }
  async listAccounts(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiLedgerAccount>>("/finance/accounts", { query: query(options) }), mapLedgerAccount); }
  async createAccount(input: LedgerAccountCreateInput) { return mapLedgerAccount(await apiRequest<ApiLedgerAccount>("/finance/accounts", { method: "POST", body: JSON.stringify(input) })); }
  async listJournals(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiJournalEntry>>("/finance/journals", { query: query(options) }), mapJournalEntry); }
  async getJournal(id: string) { return mapJournalEntry(await apiRequest<ApiJournalEntry>(`/finance/journals/${toApiId(id)}`)); }
  async createJournal(input: JournalCreateInput, idempotencyKey = crypto.randomUUID()) { return mapJournalEntry(await apiRequest<ApiJournalEntry>("/finance/journals", { method: "POST", body: JSON.stringify(input), idempotencyKey })); }
  async approveJournal(id: string) { return mapJournalEntry(await apiRequest<ApiJournalEntry>(`/finance/journals/${toApiId(id)}/approve`, { method: "POST" })); }
  async postJournal(id: string) { return mapJournalEntry(await apiRequest<ApiJournalEntry>(`/finance/journals/${toApiId(id)}/post`, { method: "POST" })); }
  async listPaymentRequests(options?: QueryOptions) { return list(await apiRequest<ApiList<ApiPaymentRequest>>("/finance/payment-requests", { query: query(options) }), mapPaymentRequest); }
  async createPaymentRequest(input: PaymentRequestCreateInput, idempotencyKey = crypto.randomUUID()) { return mapPaymentRequest(await apiRequest<ApiPaymentRequest>("/finance/payment-requests", { method: "POST", body: JSON.stringify(input), idempotencyKey })); }
  async approvePaymentRequest(id: string) { return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/finance/payment-requests/${toApiId(id)}/approve`, { method: "POST", body: "{}" })); }
  async rejectPaymentRequest(id: string) { return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/finance/payment-requests/${toApiId(id)}/reject`, { method: "POST", body: JSON.stringify({ reason: "Rejected" }) })); }
  async markPaymentPaid(id: string) { return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/finance/payment-requests/${toApiId(id)}/pay`, { method: "POST", body: "{}" })); }
}

class HttpCommercialService implements CommercialService {
  async listBuyingPrices() {
    const rows = await apiRequest<ApiBuyingPrice[]>("/commercial/buying-prices");
    return rows.map(mapBuyingPrice);
  }
  async createBuyingPrice(input: BuyingPriceCreateInput): Promise<BuyingPrice> {
    const body = { ...input, product_form: input.product_form || "DRY_CHERRY", currency: input.currency || "UGX", unit: input.unit || "kg" };
    return mapBuyingPrice(await apiRequest<ApiBuyingPrice>("/commercial/buying-prices", { method: "POST", body: JSON.stringify(body) }));
  }
  async listDeliveries(options?: QueryOptions): Promise<ListResult<CommercialDelivery>> {
    const response = await apiRequest<ApiList<ApiDelivery>>("/aggregation/deliveries", { query: query(options) });
    return { items: response.items.map(mapDelivery), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createDelivery(input: DeliveryCreateInput, idempotencyKey = crypto.randomUUID()): Promise<CommercialDelivery> {
    const body = { delivery_reference: input.delivery_reference, farmer_id: toApiId(input.farmer_id), farm_id: toApiId(input.farm_id), delivered_at: input.delivered_at, gross_quantity: input.gross_quantity, accepted_quantity: input.accepted_quantity, unit: input.unit || "kg", quality_grade: input.quality_grade, moisture_pct: input.moisture_pct, product_form: input.product_form || "DRY_CHERRY" };
    return mapDelivery(await apiRequest<ApiDelivery>("/aggregation/deliveries", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async verifyDelivery(id: string): Promise<CommercialDelivery> {
    return mapDelivery(await apiRequest<ApiDelivery>(`/aggregation/deliveries/${toApiId(id)}/verify`, { method: "POST" }));
  }
  async listTransactions(options?: QueryOptions): Promise<ListResult<CommercialTransaction>> {
    const response = await apiRequest<ApiList<ApiCommercialTxn>>("/commercial/transactions", { query: query(options) });
    return { items: response.items.map(mapCommercialTxn), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createTransaction(input: CommercialTransactionCreateInput, idempotencyKey = crypto.randomUUID()): Promise<CommercialTransaction> {
    const body = { farmer_id: toApiId(input.farmer_id), delivery_id: input.delivery_id ? toApiId(input.delivery_id) : undefined, buying_price_id: input.buying_price_id ? toApiId(input.buying_price_id) : undefined, transaction_reference: input.transaction_reference, transaction_date: input.transaction_date, currency: input.currency || "UGX", accepted_quantity: input.accepted_quantity, unit_price: input.unit_price, rule_version: input.rule_version };
    return mapCommercialTxn(await apiRequest<ApiCommercialTxn>("/commercial/transactions", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async approveTransaction(id: string): Promise<CommercialTransaction> {
    return mapCommercialTxn(await apiRequest<ApiCommercialTxn>(`/commercial/transactions/${toApiId(id)}/approve`, { method: "POST" }));
  }
  async postTransaction(id: string): Promise<CommercialTransaction> {
    return mapCommercialTxn(await apiRequest<ApiCommercialTxn>(`/commercial/transactions/${toApiId(id)}/post`, { method: "POST" }));
  }
  async listPayments(options?: QueryOptions): Promise<ListResult<FarmerPayment>> {
    const response = await apiRequest<ApiList<ApiFarmerPayment>>("/farmer-payments", { query: query(options) });
    return { items: response.items.map(mapFarmerPayment), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createPayment(input: FarmerPaymentCreateInput, idempotencyKey = crypto.randomUUID()): Promise<FarmerPayment> {
    const body = { farmer_id: toApiId(input.farmer_id), payment_reference: input.payment_reference, currency: input.currency || "UGX", amount: input.amount, payment_method: input.payment_method, transaction_ids: (input.transaction_ids || []).map(toApiId), finance_payment_request_id: input.finance_payment_request_id ? toApiId(input.finance_payment_request_id) : undefined };
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>("/farmer-payments", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async approvePayment(id: string): Promise<FarmerPayment> {
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>(`/farmer-payments/${toApiId(id)}/approve`, { method: "POST" }));
  }
  async markPaymentPaid(id: string): Promise<FarmerPayment> {
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>(`/farmer-payments/${toApiId(id)}/pay`, { method: "POST" }));
  }
  async linkFinance(id: string, financePaymentRequestId: string): Promise<FarmerPayment> {
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>(`/farmer-payments/${toApiId(id)}/link-finance?finance_payment_request_id=${toApiId(financePaymentRequestId)}`, { method: "POST" }));
  }
  async listBalances(options?: QueryOptions): Promise<ListResult<FarmerBalance>> {
    const response = await apiRequest<ApiList<ApiFarmerBalance>>("/commercial/farmer-balances", { query: query(options) });
    return { items: response.items.map(mapFarmerBalance), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
}

class HttpAccountsService implements AccountsService {
  async listProgrammeExpenses(options?: QueryOptions): Promise<ListResult<PaymentRequest>> {
    const response = await apiRequest<ApiList<ApiPaymentRequest>>("/accounts/programme-expenses", { query: query(options) });
    return list(response, mapPaymentRequest);
  }
  async createProgrammeExpense(input: ProgrammeExpenseCreateInput, idempotencyKey = crypto.randomUUID()): Promise<PaymentRequest> {
    const body = {
      ...input,
      currency: input.currency || "UGX",
      beneficiary_id: input.beneficiary_id ? toApiId(input.beneficiary_id) : undefined,
      source_entity_id: input.source_entity_id ? toApiId(input.source_entity_id) : undefined,
      ledger_account_id: input.ledger_account_id ? toApiId(input.ledger_account_id) : undefined,
      geographic_unit_id: input.geographic_unit_id ? toApiId(input.geographic_unit_id) : undefined,
    };
    return mapPaymentRequest(await apiRequest<ApiPaymentRequest>("/accounts/programme-expenses", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async submitProgrammeExpense(id: string): Promise<PaymentRequest> {
    return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/accounts/programme-expenses/${toApiId(id)}/submit`, { method: "POST", body: "{}" }));
  }
  async approveProgrammeExpense(id: string): Promise<PaymentRequest> {
    return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/accounts/programme-expenses/${toApiId(id)}/approve`, { method: "POST", body: "{}" }));
  }
  async rejectProgrammeExpense(id: string, reason: string): Promise<PaymentRequest> {
    return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/accounts/programme-expenses/${toApiId(id)}/reject`, { method: "POST", body: JSON.stringify({ reason }) }));
  }
  async cancelProgrammeExpense(id: string, reason: string): Promise<PaymentRequest> {
    return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/accounts/programme-expenses/${toApiId(id)}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }));
  }
  async payProgrammeExpense(id: string, paymentMethod?: string, externalPaymentReference?: string): Promise<PaymentRequest> {
    const body = { payment_method: paymentMethod, external_payment_reference: externalPaymentReference };
    return mapPaymentRequest(await apiRequest<ApiPaymentRequest>(`/accounts/programme-expenses/${toApiId(id)}/pay`, { method: "POST", body: JSON.stringify(body) }));
  }
  async listBuyingPrices() {
    const rows = await apiRequest<ApiBuyingPrice[]>("/accounts/buying-prices");
    return rows.map(mapBuyingPrice);
  }
  async createBuyingPrice(input: BuyingPriceCreateInput): Promise<BuyingPrice> {
    const body = { ...input, product_form: input.product_form || "DRY_CHERRY", currency: input.currency || "UGX", unit: input.unit || "kg" };
    return mapBuyingPrice(await apiRequest<ApiBuyingPrice>("/accounts/buying-prices", { method: "POST", body: JSON.stringify(body) }));
  }
  async listSettlements(options?: QueryOptions): Promise<ListResult<CommercialTransaction>> {
    const response = await apiRequest<ApiList<ApiCommercialTxn>>("/accounts/farmer-settlements", { query: query(options) });
    return { items: response.items.map(mapCommercialTxn), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createSettlement(input: CommercialTransactionCreateInput, idempotencyKey = crypto.randomUUID()): Promise<CommercialTransaction> {
    const body = { farmer_id: toApiId(input.farmer_id), delivery_id: input.delivery_id ? toApiId(input.delivery_id) : undefined, buying_price_id: input.buying_price_id ? toApiId(input.buying_price_id) : undefined, transaction_reference: input.transaction_reference, transaction_date: input.transaction_date, currency: input.currency || "UGX", accepted_quantity: input.accepted_quantity, unit_price: input.unit_price, rule_version: input.rule_version };
    return mapCommercialTxn(await apiRequest<ApiCommercialTxn>("/accounts/farmer-settlements", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async approveSettlement(id: string): Promise<CommercialTransaction> {
    return mapCommercialTxn(await apiRequest<ApiCommercialTxn>(`/accounts/farmer-settlements/${toApiId(id)}/approve`, { method: "POST" }));
  }
  async postSettlement(id: string): Promise<CommercialTransaction> {
    return mapCommercialTxn(await apiRequest<ApiCommercialTxn>(`/accounts/farmer-settlements/${toApiId(id)}/post`, { method: "POST" }));
  }
  async listFarmerPayments(options?: QueryOptions): Promise<ListResult<FarmerPayment>> {
    const response = await apiRequest<ApiList<ApiFarmerPayment>>("/accounts/farmer-payments", { query: query(options) });
    return { items: response.items.map(mapFarmerPayment), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createFarmerPayment(input: FarmerPaymentCreateInput, idempotencyKey = crypto.randomUUID()): Promise<FarmerPayment> {
    const body = { farmer_id: toApiId(input.farmer_id), payment_reference: input.payment_reference, currency: input.currency || "UGX", amount: input.amount, payment_method: input.payment_method, transaction_ids: (input.transaction_ids || []).map(toApiId), finance_payment_request_id: input.finance_payment_request_id ? toApiId(input.finance_payment_request_id) : undefined };
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>("/accounts/farmer-payments", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async approveFarmerPayment(id: string): Promise<FarmerPayment> {
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>(`/accounts/farmer-payments/${toApiId(id)}/approve`, { method: "POST" }));
  }
  async markFarmerPaymentPaid(id: string): Promise<FarmerPayment> {
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>(`/accounts/farmer-payments/${toApiId(id)}/pay`, { method: "POST" }));
  }
  async linkFinance(id: string, financePaymentRequestId: string): Promise<FarmerPayment> {
    return mapFarmerPayment(await apiRequest<ApiFarmerPayment>(`/accounts/farmer-payments/${toApiId(id)}/link-finance?finance_payment_request_id=${toApiId(financePaymentRequestId)}`, { method: "POST" }));
  }
  async listFarmerBalances(options?: QueryOptions): Promise<ListResult<FarmerBalance>> {
    const response = await apiRequest<ApiList<ApiFarmerBalance>>("/accounts/farmer-balances", { query: query(options) });
    return { items: response.items.map(mapFarmerBalance), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async listFarmerAdvances(options?: QueryOptions): Promise<ListResult<FarmerAdvance>> {
    const response = await apiRequest<ApiList<ApiFarmerAdvance>>("/accounts/farmer-advances", { query: query(options) });
    return list(response, mapFarmerAdvance);
  }
  async getFarmerAdvance(id: string): Promise<FarmerAdvance> {
    return mapFarmerAdvance(await apiRequest<ApiFarmerAdvance>(`/accounts/farmer-advances/${toApiId(id)}`));
  }
  async createFarmerAdvance(input: FarmerAdvanceCreateInput, idempotencyKey: string = crypto.randomUUID()): Promise<FarmerAdvance> {
    const body = {
      ...input,
      farmer_id: toApiId(input.farmer_id),
      farm_id: input.farm_id ? toApiId(input.farm_id) : undefined,
      harvest_season_id: input.harvest_season_id ? toApiId(input.harvest_season_id) : undefined,
      currency: input.currency || "UGX",
    };
    return mapFarmerAdvance(await apiRequest<ApiFarmerAdvance>("/accounts/farmer-advances", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async approveFarmerAdvance(id: string): Promise<FarmerAdvance> {
    return mapFarmerAdvance(await apiRequest<ApiFarmerAdvance>(`/accounts/farmer-advances/${toApiId(id)}/approve`, { method: "POST", body: "{}" }));
  }
  async disburseFarmerAdvance(id: string, input: FarmerAdvanceDisburseInput): Promise<FarmerAdvance> {
    const body = { payment_method: input.payment_method, external_payment_reference: input.external_payment_reference };
    return mapFarmerAdvance(await apiRequest<ApiFarmerAdvance>(`/accounts/farmer-advances/${toApiId(id)}/disburse`, { method: "POST", body: JSON.stringify(body) }));
  }
  async cancelFarmerAdvance(id: string, reason: string): Promise<FarmerAdvance> {
    return mapFarmerAdvance(await apiRequest<ApiFarmerAdvance>(`/accounts/farmer-advances/${toApiId(id)}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }));
  }
  async recoverFarmerAdvance(id: string, input: FarmerAdvanceRecoveryCreateInput, idempotencyKey: string = crypto.randomUUID()): Promise<FarmerAdvanceRecovery> {
    const body = {
      ...input,
      settlement_id: input.settlement_id ? toApiId(input.settlement_id) : undefined,
      currency: input.currency || "UGX",
    };
    return mapFarmerAdvanceRecovery(await apiRequest<ApiFarmerAdvanceRecovery>(`/accounts/farmer-advances/${toApiId(id)}/recover`, { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async listFarmerAdvanceRecoveries(id: string, options?: QueryOptions): Promise<ListResult<FarmerAdvanceRecovery>> {
    const response = await apiRequest<ApiList<ApiFarmerAdvanceRecovery>>(`/accounts/farmer-advances/${toApiId(id)}/recoveries`, { query: query(options) });
    return list(response, mapFarmerAdvanceRecovery);
  }
}

class HttpAgreementService implements AgreementService {
  async listAgreements(options?: QueryOptions): Promise<ListResult<FarmerAgreement>> {
    const response = await apiRequest<ApiList<ApiAgreement>>("/agreements", { query: query(options) });
    return { items: response.items.map(mapAgreement), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async getAgreement(id: string): Promise<FarmerAgreement | undefined> {
    return mapAgreement(await apiRequest<ApiAgreement>(`/agreements/${toApiId(id)}`));
  }
  async createAgreement(input: AgreementCreateInput, idempotencyKey = crypto.randomUUID()): Promise<FarmerAgreement> {
    const body = { client_generated_id: idempotencyKey, farmer_id: toApiId(input.farmer_id), agreement_type: input.agreement_type, agreement_reference: input.agreement_reference, farmer_share_pct: input.farmer_share_pct, company_share_pct: input.company_share_pct, signed_date: input.signed_date, effective_date: input.effective_date, expiry_date: input.expiry_date, status: input.status || "DRAFT", document_object_path: input.document_object_path, responsible_staff_id: input.responsible_staff_id };
    return mapAgreement(await apiRequest<ApiAgreement>("/agreements", { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async listAmendments(agreementId: string): Promise<ListResult<AgreementAmendment>> {
    const response = await apiRequest<ApiList<ApiAmendment>>(`/agreements/${toApiId(agreementId)}/amendments`, {});
    return { items: response.items.map(mapAmendment), total: response.total, page: response.page, pageSize: response.page_size, totalPages: response.total_pages };
  }
  async createAmendment(agreementId: string, input: AmendmentCreateInput, idempotencyKey = crypto.randomUUID()): Promise<AgreementAmendment> {
    const body = { client_generated_id: idempotencyKey, original_agreement_id: toApiId(agreementId), amendment_type: input.amendment_type, amendment_reference: input.amendment_reference, status: input.status || "DRAFT", farmer_share_pct: input.farmer_share_pct, company_share_pct: input.company_share_pct, term_years: input.term_years, signed_date: input.signed_date, effective_date: input.effective_date, expiry_date: input.expiry_date, document_object_path: input.document_object_path };
    return mapAmendment(await apiRequest<ApiAmendment>(`/agreements/${toApiId(agreementId)}/amendments`, { method: "POST", body: JSON.stringify(body), idempotencyKey }));
  }
  async executeAmendment(amendmentId: string, input: AmendmentExecuteInput): Promise<AgreementAmendment> {
    return mapAmendment(await apiRequest<ApiAmendment>(`/agreements/amendments/${toApiId(amendmentId)}/execute`, { method: "POST", body: JSON.stringify(input) }));
  }
}

class HttpGeographyService implements GeographyService {
  async listUnits(parentId?: string, unitType?: string): Promise<GeographicUnit[]> {
    const params: Record<string, string> = {};
    if (parentId) params.parent_id = toApiId(parentId);
    if (unitType) params.unit_type = unitType;
    const items = await apiRequest<ApiGeographicUnit[]>("/geography/units", { query: params });
    return items.map(mapGeographicUnit);
  }
  async getUnit(id: string): Promise<GeographicUnit | undefined> {
    return mapGeographicUnit(await apiRequest<ApiGeographicUnit>(`/geography/units/${toApiId(id)}`));
  }
}

class HttpDashboardService implements DashboardService {
  async getSummary(params?: { geographic_unit_id?: string; date_from?: string; date_to?: string }): Promise<DashboardSummary> {
    const query: Record<string, string> = {};
    if (params?.geographic_unit_id) query.geographic_unit_id = params.geographic_unit_id;
    if (params?.date_from) query.date_from = params.date_from;
    if (params?.date_to) query.date_to = params.date_to;
    return apiRequest<DashboardSummary>("/dashboard/summary", { query });
  }
}

export const httpServices = { farmers: new HttpFarmersService(), farms: new HttpFarmsService(), staff: new HttpStaffService(), visits: new HttpVisitsService(), inputs: new HttpInputsService(), nursery: new HttpNurseryService(), inventory: new HttpInventoryService(), harvest: new HttpHarvestService(), media: new HttpMediaService(), transactions: new HttpTransactionsService(), assets: new HttpAssetsService(), finance: new HttpFinanceService(), accounts: new HttpAccountsService(), commercial: new HttpCommercialService(), agreements: new HttpAgreementService(), geography: new HttpGeographyService(), dashboard: new HttpDashboardService() };
